import React, { useMemo } from "react";
import TableHeaderCategories from "./components/TableHeaderCategories";
import { TableLoading } from "./components/TableLoading";
import ContentCard from "../misc/ContentCard";
import TableGlobalSearch from "./components/TableGlobalSearch";
import TableGlobalFilter from "./components/TableGlobalFilter";
import TableProvider from "./tableContext/TableProvider";
import {
  TableActions,
  TableData,
  TableFilterDataItem,
  TableHeaderCategoryConfig,
} from "./tableContext/TableContext";
import TablePagination from "./components/TablePagination";
import TableHeader from "./components/TableHeader";
import TableBody from "./components/TableBody";
import useTableConfig from "./hooks/useTableConfig";
import { useSearchParams } from "react-router-dom";

export type TableItemId = string | number;

type Props = {
  className?: string;
  tableHeaderData: TableHeaderCategoryConfig[];
  tableItemsData: TableData[];
  globalFilter?: TableFilterDataItem[];
  withGlobalSearch?:
    | boolean
    | {
        placeholder?: string;
        searchDelay?: number;
      };
  withGlobalFilter?: boolean;
  borderedRow?: boolean;
  ActionButtonsComponent?: any;
  CustomHeaderComponent?: any;
  tableTitle?: string;
  showSearchForCategories?: boolean;
  withTablePagination?: boolean;
  handleTableActions?: (value: TableActions) => void;
  handleUnmount?: () => void;
  tableLoading?: boolean;
  totalItems?: number;
  emptyTableLabel?: string;
};

const EmptyCardHeader = () => <></>;

const TableWidget: React.FC<Props> = (props) => {
  const [searchRouteParams] = useSearchParams();

  const initialTablePagination = useMemo(() => {
    const page = searchRouteParams.get("page");
    const rowsPerPage = searchRouteParams.get("rowsPerPage");
    if (!page && !rowsPerPage) return undefined;
    return {
      page: page ? parseInt(page) : 1,
      rowsPerPage: rowsPerPage ? parseInt(rowsPerPage) : 25,
    };
  }, [searchRouteParams]);

  const initialTableSearch = useMemo(() => {
    const search: Record<string, string> = {};
    searchRouteParams.forEach((value, key) => {
      if (key !== "page" && key !== "rowsPerPage") {
        search[key] = value;
      }
    });
    return Object.keys(search).length > 0 ? search : undefined;
  }, [searchRouteParams]);

  return (
    <TableProvider
      initialTableSearch={initialTableSearch}
      initialTablePagination={initialTablePagination}>
      <TableComponent {...props} />
    </TableProvider>
  );
};

const TableComponent: React.FC<Props> = ({
  globalFilter,
  tableHeaderData,
  tableItemsData,
  className,
  withGlobalSearch,
  withGlobalFilter,
  borderedRow = true,
  ActionButtonsComponent,
  CustomHeaderComponent,
  tableTitle,
  showSearchForCategories = true,
  withTablePagination = true,
  handleTableActions,
  handleUnmount,
  tableLoading,
  totalItems,
  emptyTableLabel = "Tabelul este gol",
}) => {
  useTableConfig({
    handleTableActions,
    handleUnmount,
    globalFilter,
    tableHeaderData,
    tableItemsData,
  });

  return (
    <>
      <ContentCard
        cardBodyClassName={`card w-100 ${className}`}
        cardBodyStyle={{ padding: 0, margin: 0 }}
        cardHeaderClassName="card-header border-0 py-5 ps-1 text-left"
        cardTitleStyle={{ fontWeight: "bolder" }}
        cardTitle={tableTitle}
        CardHeader={EmptyCardHeader}>
        <div className="table-responsive">
          {(withGlobalSearch || withGlobalFilter) && (
            <div className="d-flex">
              {withGlobalSearch && (
                <TableGlobalSearch withGlobalSearch={withGlobalSearch} />
              )}
              {withGlobalFilter && <TableGlobalFilter />}
            </div>
          )}
          <TableHeader CustomHeaderComponent={CustomHeaderComponent} />
          <table
            className={`table align-middle gs-0 gy-3 table-row-gray-100 ${
              borderedRow && "table-row-bordered"
            }`}>
            <TableHeaderCategories
              showActionButtons={!!ActionButtonsComponent}
              showSearchForCategories={showSearchForCategories}
            />
            <TableBody
              ActionButtonsComponent={ActionButtonsComponent}
              emptyTableLabel={emptyTableLabel}
            />
          </table>
        </div>
        {withTablePagination && <TablePagination totalItems={totalItems} />}
        {tableLoading && <TableLoading />}
      </ContentCard>
    </>
  );
};

export default TableWidget;
